import { position } from '../floating.js';

/**
 * <mellow-status-history>: a row of status bars (e.g. recent checks). It is a single tab
 * stop; arrow keys, Home and End move between the entries (roving tabindex). A tooltip
 * shows the entry's label on hover and focus.
 */
export class MellowStatusHistory extends HTMLElement {
    connectedCallback() {
        if (this._ready) return this.sync();
        this._ready = true;

        this.addEventListener('keydown', this.onKeydown);
        this.addEventListener('focusin', (event) => this.show(event.target.closest('[role="listitem"]')));
        this.addEventListener('focusout', (event) => {
            if (!this.contains(event.relatedTarget)) this.hide();
        });
        this.addEventListener('pointerover', (event) => {
            const bar = event.target.closest('[role="listitem"]');
            if (bar) this.show(bar);
        });
        this.addEventListener('pointerleave', () => {
            // Keep the tooltip of the focused entry for keyboard users.
            const focused = this.contains(document.activeElement) ? document.activeElement.closest('[role="listitem"]') : null;
            focused ? this.show(focused) : this.hide();
        });

        this.sync();
    }

    get bars() {
        return [...this.querySelectorAll('[role="listitem"]')];
    }

    get tooltip() {
        return this.querySelector('.mellow-status-history-tooltip');
    }

    /** Exactly one tab stop – also after Livewire replaced the bars. */
    sync() {
        const bars = this.bars;
        if (!bars.length) return;
        const current = bars.find((bar) => bar.tabIndex === 0 && bar === document.activeElement)
            ?? bars.find((bar) => bar === document.activeElement)
            ?? bars.at(-1);
        bars.forEach((bar) => {
            const index = bar === current ? '0' : '-1';
            if (bar.getAttribute('tabindex') !== index) bar.setAttribute('tabindex', index);
        });
    }

    show(bar) {
        const tooltip = this.tooltip;
        if (!bar || !tooltip) return;

        tooltip.textContent = bar.dataset.label ?? '';
        if (!tooltip.textContent) return this.hide();

        this.querySelectorAll('[data-active]').forEach((el) => el !== bar && el.removeAttribute('data-active'));
        bar.setAttribute('data-active', '');

        if (!tooltip.matches(':popover-open')) tooltip.showPopover();
        tooltip.dataset.side = position(bar, tooltip, { placement: 'top', offset: 6 }) ?? 'top';
    }

    hide() {
        this.querySelectorAll('[data-active]').forEach((el) => el.removeAttribute('data-active'));
        if (this.tooltip?.matches(':popover-open')) this.tooltip.hidePopover();
    }

    onKeydown = (event) => {
        const bars = this.bars;
        const index = bars.indexOf(event.target.closest('[role="listitem"]'));
        if (index === -1) return;

        const rtl = getComputedStyle(this).direction === 'rtl';
        const target = {
            [rtl ? 'ArrowLeft' : 'ArrowRight']: index + 1,
            [rtl ? 'ArrowRight' : 'ArrowLeft']: index - 1,
            Home: 0,
            End: bars.length - 1,
        }[event.key];

        if (event.key === 'Escape') return this.hide();
        if (target === undefined) return;

        event.preventDefault();
        const next = bars[Math.max(0, Math.min(bars.length - 1, target))];
        bars.forEach((bar) => bar.setAttribute('tabindex', bar === next ? '0' : '-1'));
        next.focus();
    };
}
